import { Button, Divider } from '@heroui/react';
import React, { memo, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faChevronRight, faAdd } from '@fortawesome/free-solid-svg-icons';
import Label from '../components/Label';
import SearchableListbox from '../components/portfolio-main/Searchable-Listbox';
import { useAppDispatch, useAppSelector } from '../../../core/rootHooks';
import { AppDispatch } from '../../../core/store';
import {
	selectIsPortfolioLoading,
	selectPortfoliosBelongingToUser,
	selectPortfoliosFetched,
	selectPortfoliosSharedWithUser,
} from '../portfolioSelectors';
import ModalManager from '../../../shared/components/modal/ModalManager';
import {
	deleteDraftPortfolioRequest,
	deletePortfolioRequest,
	displayOutputByPortfolioIdRequest,
	getPortfoliosRequest,
	getPortfolioWithVesselsRequest,
	setSelectedPortfolioRequest,
} from '../portfolioSlice';
import { IPortfolioForm, IPortfolioWithVesselsForm } from '../portfolioTypes';
import { addSeascapeLogActivity } from '../../../activity-logging/activityLogApi';
import { SeascapeActivityLogConstants } from '../../../activity-logging/activityLogTypes';

export interface IPortfolioMainPanelProps {
	title?: string;
	displayPortfolioDetailPanel: (value: boolean, portfolioId: number | null, tempId?: string, isDraft?: boolean) => void;
	displayPortfolioMainPanel: (value: string) => void;
	isDetailOpen: boolean;
	selectedPortfolio?: IPortfolioWithVesselsForm | null;
}

const PortfolioMainPanel: React.FC<IPortfolioMainPanelProps> = ({
	displayPortfolioDetailPanel,
	displayPortfolioMainPanel,
	title,
	selectedPortfolio,
	isDetailOpen,
}) => {
	const dispatch = useAppDispatch<AppDispatch>();
	const portfoliosSharedWithUser = useAppSelector(selectPortfoliosSharedWithUser);
	const portfoliosBelongingToUser = useAppSelector(selectPortfoliosBelongingToUser);
	const isLoading = useAppSelector(selectIsPortfolioLoading);
	const isFetched = useAppSelector(selectPortfoliosFetched);

	useEffect(() => {
		if (isFetched === false && portfoliosSharedWithUser.length === 0 && portfoliosBelongingToUser.length === 0) {
			dispatch(getPortfoliosRequest());
		}
	}, [dispatch, portfoliosSharedWithUser.length, portfoliosBelongingToUser.length, isFetched]);

	const selectAndEditPortfolio = useCallback(
		(item: IPortfolioForm) => {
			displayPortfolioDetailPanel(true, item.id, item.tempId, item.isDraft);
		},
		[displayPortfolioDetailPanel],
	);

	const handleAddNewPortfolioOnClick = useCallback(() => {
		displayPortfolioDetailPanel(true, null);
	}, [displayPortfolioDetailPanel]);

	const deletePortfolio = useCallback(
		(item: IPortfolioForm) => {
			ModalManager.show({
				header: 'Confirmation',
				message: 'Do you want to continue to delete selected portfolio?',
				onConfirm: () => {
					displayPortfolioDetailPanel(false, null);
					if (item.id) {
						dispatch(deletePortfolioRequest({ id: item.id }));
					} else {
						dispatch(deleteDraftPortfolioRequest({ tempId: item.tempId ?? '' }));
					}
				},
			});
		},
		[dispatch, displayPortfolioDetailPanel],
	);

	const handleClosePortfolioMainOnClick = useCallback(() => {
		displayPortfolioMainPanel('');
		dispatch(setSelectedPortfolioRequest({ id: null }));
	}, [dispatch, displayPortfolioMainPanel]);

	const clickPortfolioLink = useCallback(
		(id: number | string) => {
			dispatch(displayOutputByPortfolioIdRequest({ id }));
			displayPortfolioMainPanel('');

			const personalPortfolio = portfoliosBelongingToUser?.find((f) => f.id === id);

			if (personalPortfolio === undefined || personalPortfolio === null) {
				const teamPortfolio = portfoliosSharedWithUser?.find((f) => f.id === id);
				const payload = JSON.stringify({ PortfolioId: id, Name: teamPortfolio?.name, Scope: 'Team' });
				const eventId = SeascapeActivityLogConstants.PORTFOLIO_APPLY;
				addSeascapeLogActivity({ eventId, payload });
			} else {
				const payload = JSON.stringify({ PortfolioId: id, Name: personalPortfolio.name, Scope: 'Personal' });
				const eventId = SeascapeActivityLogConstants.PORTFOLIO_APPLY;
				addSeascapeLogActivity({ eventId, payload });
			}
		},
		[dispatch, displayPortfolioMainPanel, portfoliosBelongingToUser, portfoliosSharedWithUser],
	);

	return (
		<div className="flex flex-row h-full w-full">
			{/* <div className='flex-1'>
                <Divider className="my-0 bg-gray-200" orientation="vertical" />
            </div> */}
			<div className="flex flex-col w-full">
				<div className="flex-initial px-3 py-1 text-center justify-between">
					<Label title={title} textSize="lg" />
					<span
						className="cursor-pointer float-right mt-0.5"
						onClick={() => (isDetailOpen ? displayPortfolioDetailPanel(false, null) : handleClosePortfolioMainOnClick())}
					>
						<FontAwesomeIcon icon={isDetailOpen ? faChevronRight : faClose} size="sm" />
					</span>
				</div>
				<Divider className="w-auto mx-2 mt-1" />
				<div className="flex flex-col h-full overflow-y-hidden mt-1 mb-2 space-y-2 mx-2">
					<div className="flex-initial flex items-center justify-between">
						<Label title="Personal Portfolios" textSize="sm" />
						<Button
							size="sm"
							isDisabled={isLoading}
							variant="ghost"
							onPress={handleAddNewPortfolioOnClick}
							className={`cursor-pointer rounded border ml-4 h-5 font-semibold text-xs text-msi-midblue-color-500 ${selectedPortfolio?.id === null && isDetailOpen ? 'bg-[#D4D4D8]' : 'bg-transparent'}`}
							endContent={<FontAwesomeIcon icon={faAdd} size="sm" />}
						>
							Add New
						</Button>
					</div>
					<div className="flex flex-col flex-1 overflow-y-hidden w-full max-w-[350px]">
						<SearchableListbox
							key="personal-portfolio"
							spinnerLabel="Personal portfolios are loading"
							isLoading={isLoading}
							isDetailPanelOpen={isDetailOpen}
							selectedItem={selectedPortfolio}
							type="personal"
							clickItemLink={clickPortfolioLink}
							deleteItem={deletePortfolio}
							editItem={selectAndEditPortfolio}
							refreshItem={(id) => dispatch(getPortfolioWithVesselsRequest({ id: id }))}
							items={portfoliosBelongingToUser}
						/>
					</div>
					<Label title="Team Portfolios" textSize="sm" />
					<div className="flex flex-col flex-1 overflow-y-hidden w-full max-w-[300px]">
						<SearchableListbox
							key="team-portfolio"
							spinnerLabel="Team portfolios are loading"
							isLoading={isLoading}
							isDetailPanelOpen={isDetailOpen}
							selectedItem={selectedPortfolio}
							type="team"
							clickItemLink={clickPortfolioLink}
							editItem={selectAndEditPortfolio}
							refreshItem={(id) => dispatch(getPortfolioWithVesselsRequest({ id: id }))}
							items={portfoliosSharedWithUser}
							deleteItem={deletePortfolio}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default memo(PortfolioMainPanel);
